import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import {
  Input,
  Listbox,
  ListboxSection,
  ListboxItem,
  Spinner,
} from "@heroui/react";
import { Tag, Plus, Sparkle } from "@phosphor-icons/react";
import DropdownPortal from "./dropdownPortal";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import ProposeLabelModal from "./ProposeLabelModal";

export type FileLabelResourceType = 'building' | 'unit' | 'attribute';
export type FileLabelDocumentType = 'document' | 'picture';

const FileLabelSearchComponent = ({
  incomingSelectedLabel,
  onSelect,
  incomingResourceTypes,
  incomingDocumentTypes,
  customLabels,
  labelType = 'simple',
  placeholder = 'Zoek bestandslabels',
  allowCreateNew = true,
}: {
  incomingSelectedLabel: string | null;
  onSelect: (label: any) => void;
  incomingResourceTypes?: FileLabelResourceType[] | ['building', 'unit'];
  incomingDocumentTypes?: FileLabelDocumentType[] | ['document', 'picture'];
  customLabels?: string[];
  labelType?: 'simple' | 'full';
  placeholder?: string;
  /** Allow creating new labels when no match found */
  allowCreateNew?: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState(incomingSelectedLabel || "");
  const [filteredLabels, setFilteredLabels] = useState<any[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resourceTypes, setResourceTypes] = useState<FileLabelResourceType[] | null>(incomingResourceTypes || ['building', 'unit']);
  const [documentTypes, setDocumentTypes] = useState<FileLabelDocumentType[] | null>(incomingDocumentTypes || ['document', 'picture']);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const { session } = useSession();

  // State for propose label modal
  const [showProposeLabelModal, setShowProposeLabelModal] = useState(false);
  const [proposedLabelName, setProposedLabelName] = useState("");

  useEffect(() => {
    console.log(customLabels)
    setSearchTerm(incomingSelectedLabel || "");
    setIsDropdownVisible(false);
  }, [incomingSelectedLabel]);

  useEffect(() => {
    const keepClosed = true;
    setResourceTypes(incomingResourceTypes || ['building', 'unit']);
    setSearchTerm(incomingSelectedLabel || "");
    handleSearchChange(incomingSelectedLabel || "", keepClosed);
  }, [incomingResourceTypes]);

  useEffect(() => {
    const keepClosed = true;
    setDocumentTypes(incomingDocumentTypes || ['document', 'picture']);
    setSearchTerm(incomingSelectedLabel || "");
    handleSearchChange(incomingSelectedLabel || "", keepClosed);
  }, [incomingDocumentTypes]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as any).contains(event.target) &&
        inputRef.current &&
        !(inputRef.current as any).contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };

    console.log(customLabels)

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const handleSearchChange = debounce(async (term: string, keepClosed: boolean = false) => {
    setIsLoading(true);

    if (customLabels && customLabels.length > 0) {
      const filtered = customLabels
        .map(label => typeof label === 'string' ? { label } : label)  // Ensure we have an object with a label property
        .filter((labelObj: any) =>
          labelObj.label.toLowerCase().includes(term.toLowerCase())
        )
        .sort((a, b) => a.label.localeCompare(b.label)); // Alphabetical sort
      setFilteredLabels(filtered);
      if (!keepClosed) {
        setIsDropdownVisible(true);
      }
    } else {
      try {
        const queryParams = new URLSearchParams({
          term,
          resourceTypes: (resourceTypes && resourceTypes.join(',')) || (incomingResourceTypes && incomingResourceTypes.join(',')) || ['building', 'unit'].join(','),
          documentTypes: (documentTypes && documentTypes.join(',')) || (incomingDocumentTypes && incomingDocumentTypes.join(',')) || ['document', 'picture'].join(','),
        });
        const response = await fetchWithAuth(
          `${API_URL}/file/search-labels?${queryParams}`,
          { method: "GET" },
          session
        );
        if (!response.ok) throw new Error('Failed to fetch labels');
        const labels = await response.json();
        // Sort the labels alphabetically by their label property
        const sortedLabels = labels.sort((a: any, b: any) => a.label.localeCompare(b.label));
        setFilteredLabels(sortedLabels);
        if (!keepClosed) {
          setIsDropdownVisible(true);
        }
      } catch (error) {
        console.error('Error fetching labels:', error);
        setFilteredLabels([]);
      }
    }

    setIsLoading(false);
  }, 300);

  const handleSearch = (e: any) => {
    setSearchTerm(e.target.value);
    handleSearchChange(e.target.value);
  };

  const handleSelect = (label: string) => {
    setSearchTerm("");
    onSelect(label);
    setSearchTerm("");
    setIsDropdownVisible(false);
  };

  // Check if search term has an exact match in filtered labels
  const hasExactMatch = filteredLabels.some(
    (label: any) => label.label.toLowerCase() === searchTerm.toLowerCase()
  );

  // Show "create new" option if no exact match and search term is not empty
  const showCreateNewOption = allowCreateNew && !customLabels && searchTerm.trim().length > 0 && !hasExactMatch;

  // Handle opening the propose label modal
  const handleOpenProposeLabelModal = () => {
    setProposedLabelName(searchTerm.trim());
    setShowProposeLabelModal(true);
    setIsDropdownVisible(false);
  };

  // Handle when a new label is created
  const handleLabelCreated = (newLabel: any) => {
    // Select the newly created label
    if (labelType === "full") {
      handleSelect(newLabel);
    } else {
      handleSelect(newLabel.label);
    }
    setShowProposeLabelModal(false);
  };

  return (
    <div className="relative h-fit" ref={dropdownRef}>
      <div className="flex justify-center gap-4">
        <Input
          ref={inputRef}
          startContent={
            isLoading ? (
              <Spinner size="sm" color="warning" />
            ) : (
              <Tag weight="duotone" size={18} />
            )
          }
          placeholder={placeholder}
          classNames={{
            input: "text-neutral-primary placeholder:text-neutral-secondary-mid",
            inputWrapper:
              "p-2 min-h-fit h-fit rounded-full bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
          }}
          value={searchTerm}
          onFocus={() => setIsDropdownVisible(true)}
          onChange={handleSearch}
        />
      </div>
      {isDropdownVisible && (filteredLabels.length > 0 || showCreateNewOption) && (
        <DropdownPortal targetRef={inputRef}>
          <Listbox
            variant="flat"
            color="success"
            aria-label="File labels"
            className="absolute left-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-lg border border-neutral-primary/20 bg-white p-2 shadow-md min-w-[250px] w-auto max-w-full w-full"
            items={[
              ...(filteredLabels.length > 0 ? [{ key: 'existing-labels' }] : []),
              ...(showCreateNewOption ? [{ key: 'create-new-label' }] : []),
            ]}
          >
            {filteredLabels.length > 0 ? (
              <ListboxSection
                title="Bestaande labels"
                classNames={{
                  base: "mb-2",
                  heading: "font-medium uppercase tracking-wider text-xs text-neutral-secondary-mid px-2",
                }}
              >
                {filteredLabels.map((label: any) => (
                  <ListboxItem
                    key={label.label}
                    onClick={() => {
                      if (labelType === "full") {
                        handleSelect(label);
                      } else {
                        handleSelect(label.label);
                      }
                    }}
                    classNames={{
                      base: "mb-1.5 flex items-center gap-2",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {label.resourceType === "unit" && (
                        <span
                          style={{
                            backgroundColor: "#5B7304",
                            color: "#ffffff",
                            borderRadius: "50%",
                            width: "18px",
                            height: "18px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                            transition: "none",
                            opacity: 1,
                          }}
                        >
                          U
                        </span>
                      )}
                      {label.resourceType === "building" && (
                        <span
                          style={{
                            backgroundColor: "#F9F0EE",
                            color: "#31403B",
                            borderRadius: "50%",
                            width: "18px",
                            height: "18px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                            transition: "none",
                            opacity: 1,
                          }}
                        >
                          G
                        </span>
                      )}
                      {label.resourceType === "attribute" && (
                        <span
                          style={{
                            backgroundColor: "#31403B",
                            color: "#ffffff",
                            borderRadius: "50%",
                            width: "18px",
                            height: "18px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                            transition: "none",
                            opacity: 1,
                          }}
                        >
                          A
                        </span>
                      )}
                      {label.label}
                    </div>
                  </ListboxItem>
                ))}
              </ListboxSection>
            ) : null}
            {showCreateNewOption ? (
              <ListboxSection
                title="Nieuw label"
                classNames={{
                  base: "mt-2 pt-2 border-t border-neutral-primary/10",
                  heading: "font-medium uppercase tracking-wider text-xs text-violet-600 px-2",
                }}
              >
                <ListboxItem
                  key="create-new"
                  onClick={handleOpenProposeLabelModal}
                  classNames={{
                    base: "mb-1.5 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        backgroundColor: "#7c3aed",
                        color: "#ffffff",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                      }}
                    >
                      <Plus size={10} weight="bold" />
                    </span>
                    <span className="flex-1">
                      <span className="font-medium text-violet-700">"{searchTerm}"</span>
                      <span className="text-violet-600 ml-1">aanmaken</span>
                    </span>
                    <Sparkle size={14} weight="fill" className="text-violet-500" />
                  </div>
                </ListboxItem>
              </ListboxSection>
            ) : null}
          </Listbox>
        </DropdownPortal>
      )}

      {/* Propose Label Modal */}
      <ProposeLabelModal
        isOpen={showProposeLabelModal}
        onClose={() => setShowProposeLabelModal(false)}
        proposedLabelName={proposedLabelName}
        suggestedResourceType={resourceTypes?.[0] || 'building'}
        suggestedDocumentType={documentTypes?.[0] || 'document'}
        onLabelCreated={handleLabelCreated}
      />
    </div>
  );
};

export default FileLabelSearchComponent;